<template>
  <div class="my-reservations-wrapper">
    <!-- Top Navigation -->
    <div class="top-navigation">
      <div class="nav-left">
        <el-button text @click="goToHome">
          <el-icon><HomeFilled /></el-icon>
          {{ t('myReservations.backToHome') }}
        </el-button>
      </div>
      <div class="nav-right">
        <el-dropdown @command="handleLanguageChange">
          <span class="el-dropdown-link">
            {{ currentLanguageName }}
            <el-icon class="el-icon--right">
              <arrow-down />
            </el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="zh-TW">{{ t('language.zhTW') }}</el-dropdown-item>
              <el-dropdown-item command="zh-CN">{{ t('language.zhCN') }}</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>

    <div class="reservations-container">
      <!-- Page Header -->
      <div class="page-header">
        <h1 class="page-title">{{ t('myReservations.title') }}</h1>
        <p class="page-subtitle">{{ t('myReservations.subtitle') }}</p>
      </div>

      <!-- Search Form -->
      <div class="search-form-section">
        <div class="search-form">
          <el-form :model="searchForm" label-position="top">
            <el-form-item :label="t('myReservations.searchBy')">
              <el-input
                v-model="searchForm.phone"
                :placeholder="t('myReservations.phonePlaceholder')"
                clearable
              >
                <template #prefix>
                  <el-icon><Phone /></el-icon>
                </template>
              </el-input>
            </el-form-item>
            
            <el-form-item>
              <el-input
                v-model="searchForm.email"
                :placeholder="t('myReservations.emailPlaceholder')"
                clearable
              >
                <template #prefix>
                  <el-icon><Message /></el-icon>
                </template>
              </el-input>
            </el-form-item>

            <el-form-item>
              <el-button type="primary" @click="searchReservations" size="large" style="width: 100%;">
                {{ t('myReservations.search') }}
              </el-button>
            </el-form-item>
          </el-form>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="loading-container">
        <el-icon class="is-loading" :size="40">
          <Loading />
        </el-icon>
      </div>

      <!-- Reservations List -->
      <div v-else-if="reservations.length > 0" class="reservations-list">
        <div
          v-for="reservation in reservations"
          :key="reservation.reservationId"
          class="reservation-card"
        >
          <div class="card-header">
            <div class="reservation-id">
              {{ t('myReservations.caseNo') }}: {{ reservation.caseNo }}
            </div>
            <el-tag :type="getStatusType(reservation.status)">
              {{ reservation.statusName || getStatusText(reservation.status) }}
            </el-tag>
          </div>

          <div class="card-body">
            <div class="info-row">
              <span class="label">{{ t('form.name') }}:</span>
              <span class="value">{{ reservation.realName }}</span>
            </div>
            <div class="info-row">
              <span class="label">{{ t('form.phone') }}:</span>
              <span class="value">{{ reservation.phone }}</span>
            </div>
            <div class="info-row">
              <span class="label">{{ t('form.address') }}:</span>
              <span class="value">{{ reservation.address }}</span>
            </div>
            <div class="info-row">
              <span class="label">{{ t('form.repairType') }}:</span>
              <span class="value">{{ reservation.repairType }}</span>
            </div>
            <div class="info-row">
              <span class="label">{{ t('myReservations.createTime') }}:</span>
              <span class="value">{{ reservation.createTime }}</span>
            </div>
          </div>

          <div class="card-footer">
            <el-button size="small" @click="viewDetail(reservation.reservationId)">
              {{ t('myReservations.viewDetail') }}
            </el-button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <el-empty :description="t('myReservations.noData')" />
      </div>
    </div>

    <!-- Detail Dialog -->
    <el-dialog
      v-model="detailDialogVisible"
      :title="t('myReservations.detailTitle')"
      width="90%"
      style="max-width: 800px"
    >
      <div v-if="currentDetail" class="detail-content">
        <!-- Current Stage & Status -->
        <div class="detail-section" v-if="currentDetail.currentStage">
          <div class="detail-row">
            <span class="detail-label">{{ t('myReservations.currentStage') }}:</span>
            <el-tag :type="getStatusType(currentDetail.status)" size="large">
              {{ currentDetail.statusName || getStatusText(currentDetail.status) }}
            </el-tag>
          </div>
        </div>

        <!-- Order Demand Info -->
        <div class="detail-section" v-if="currentDetail.orderDemand">
          <h3>{{ t('myReservations.basicInfo') }}</h3>
          <div class="detail-row">
            <span class="detail-label">{{ t('myReservations.caseNo') }}:</span>
            <span>{{ currentDetail.orderDemand.caseNo }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ t('form.name') }}:</span>
            <span>{{ currentDetail.orderDemand.realName }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ t('form.phone') }}:</span>
            <span>{{ currentDetail.orderDemand.phone }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ t('form.address') }}:</span>
            <span>{{ currentDetail.orderDemand.address }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ t('form.repairType') }}:</span>
            <span>{{ currentDetail.orderDemand.repairType }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ t('form.description') }}:</span>
            <span>{{ currentDetail.orderDemand.supplementDesc || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ t('myReservations.createTime') }}:</span>
            <span>{{ currentDetail.orderDemand.createTime }}</span>
          </div>
          <!-- Order Demand Images/Videos -->
          <div v-if="currentDetail.orderDemand.images && currentDetail.orderDemand.images.length > 0" style="margin-top: 16px;">
            <div class="images-grid">
              <div v-for="media in currentDetail.orderDemand.images" :key="media.imageId" class="media-item">
                <template v-if="isVideo(media.fileType)">
                  <div class="video-thumbnail" @click="previewMedia(media.imageUrl, media.fileType)">
                    <video :src="media.imageUrl" class="detail-video-thumb"></video>
                    <div class="play-icon"><el-icon><VideoPlay /></el-icon></div>
                  </div>
                </template>
                <img v-else :src="media.imageUrl" :alt="media.fileName || 'Image'" class="detail-image" @click="previewMedia(media.imageUrl, media.fileType)" />
              </div>
            </div>
          </div>
        </div>

        <!-- Customer Confirm Actions (for PENDING_CUSTOMER_CONFIRM status) -->
        <div class="detail-section confirm-actions-section" v-if="currentDetail.status === 'PENDING_CUSTOMER_CONFIRM'">
          <div class="confirm-notice">
            <el-icon><WarningFilled /></el-icon>
            <span>{{ t('myReservations.pendingConfirmNotice') }}</span>
          </div>
          <div class="confirm-buttons">
            <el-button type="success" size="large" @click="confirmQuotation" :loading="confirmLoading">
              <el-icon><Check /></el-icon>
              {{ t('myReservations.confirmQuotation') }}
            </el-button>
            <el-button type="danger" size="large" @click="showRejectDialog">
              <el-icon><Close /></el-icon>
              {{ t('myReservations.rejectQuotation') }}
            </el-button>
          </div>
        </div>

        <!-- Quotation Stage -->
        <div class="detail-section" v-if="currentDetail.quotationStage">
          <h3>{{ t('myReservations.quotationStage') }}</h3>
          <div v-if="currentDetail.quotationStage.mainQuotations && currentDetail.quotationStage.mainQuotations.length > 0">
            <div v-for="quote in currentDetail.quotationStage.mainQuotations" :key="quote.quotationId" class="quote-item">
              <div class="detail-row">
                <span class="detail-label">{{ t('myReservations.quotedAmount') }}:</span>
                <span class="amount">NT$ {{ quote.quotedAmount }}</span>
              </div>
              <div class="detail-row" v-if="quote.workDescription">
                <span class="detail-label">{{ t('myReservations.quotationDesc') }}:</span>
                <span>{{ quote.workDescription }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">{{ t('myReservations.quotationTime') }}:</span>
                <span>{{ quote.quotedTime }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">{{ t('myReservations.status.label') }}:</span>
                <span>{{ quote.statusName }}</span>
              </div>
              <div v-if="quote.images && quote.images.length > 0" style="margin-top: 8px;">
                <div class="images-grid">
                  <div v-for="image in quote.images" :key="image.imageId" class="image-item">
                    <img :src="image.imageUrl" class="detail-image" @click="previewMedia(image.imageUrl, image.fileType)" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Sub Quotations -->
          <div v-if="currentDetail.quotationStage.subQuotations && currentDetail.quotationStage.subQuotations.length > 0">
            <div v-for="quote in currentDetail.quotationStage.subQuotations" :key="quote.quotationId" class="quote-item">
              <div class="detail-row">
                <span class="detail-label">{{ t('myReservations.quotedAmount') }}:</span>
                <span class="amount">NT$ {{ quote.quotedAmount }}</span>
              </div>
              <div class="detail-row" v-if="quote.workDescription">
                <span class="detail-label">{{ t('myReservations.quotationDesc') }}:</span>
                <span>{{ quote.workDescription }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">{{ t('myReservations.quotationTime') }}:</span>
                <span>{{ quote.quotedTime }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">{{ t('myReservations.status.label') }}:</span>
                <span>{{ quote.statusName }}</span>
              </div>
              <div v-if="quote.images && quote.images.length > 0" style="margin-top: 8px;">
                <div class="images-grid">
                  <div v-for="image in quote.images" :key="image.imageId" class="image-item">
                    <img :src="image.imageUrl" class="detail-image" @click="previewMedia(image.imageUrl, image.fileType)" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="detail-row" v-if="currentDetail.quotationStage.totalApprovedAmount" style="margin-top: 16px; padding-top: 16px; border-top: 2px solid #673ab7;">
            <span class="detail-label">{{ t('myReservations.totalApprovedAmount') }}:</span>
            <span class="amount-total">NT$ {{ currentDetail.quotationStage.totalApprovedAmount }}</span>
          </div>
        </div>

        <!-- Material Stage -->
        <div class="detail-section" v-if="currentDetail.materialStage && currentDetail.materialStage.materials && currentDetail.materialStage.materials.length > 0">
          <h3>{{ t('myReservations.materialStage') }}</h3>
          <div v-for="material in currentDetail.materialStage.materials" :key="material.materialId" class="material-item">
            <div class="detail-row">
              <span class="detail-label">{{ t('myReservations.materialName') }}:</span>
              <span>{{ material.materialName }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">{{ t('myReservations.materialPrice') }}:</span>
              <span>NT$ {{ material.materialPrice }}</span>
            </div>
            <div class="detail-row" v-if="material.remark">
              <span class="detail-label">{{ t('myReservations.remark') }}:</span>
              <span>{{ material.remark }}</span>
            </div>
            <div v-if="material.images && material.images.length > 0" style="margin-top: 8px;">
              <div class="images-grid">
                <div v-for="image in material.images" :key="image.imageId" class="image-item">
                  <img :src="image.imageUrl" class="detail-image" @click="previewMedia(image.imageUrl, image.fileType)" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Construction Stage -->
        <div class="detail-section" v-if="currentDetail.constructionStage">
          <h3>{{ t('myReservations.constructionStage') }}</h3>
          
          <!-- Before Construction -->
          <div v-if="currentDetail.constructionStage.beforeRecords && currentDetail.constructionStage.beforeRecords.length > 0">
            <h4>{{ t('myReservations.beforeConstruction') }}</h4>
            <div v-for="record in currentDetail.constructionStage.beforeRecords" :key="record.recordId" class="construction-record">
              <div class="detail-row">
                <span class="detail-label">{{ t('myReservations.description') }}:</span>
                <span>{{ record.stageDesc }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">{{ t('myReservations.recordTime') }}:</span>
                <span>{{ record.createTime }}</span>
              </div>
              <div v-if="record.images && record.images.length > 0">
                <div class="images-grid">
                  <div v-for="image in record.images" :key="image.imageId" class="image-item">
                    <img :src="image.imageUrl" class="detail-image" @click="previewMedia(image.imageUrl, image.fileType)" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- During Construction -->
          <div v-if="currentDetail.constructionStage.duringRecords && currentDetail.constructionStage.duringRecords.length > 0">
            <h4>{{ t('myReservations.duringConstruction') }}</h4>
            <div v-for="record in currentDetail.constructionStage.duringRecords" :key="record.recordId" class="construction-record">
              <div class="detail-row">
                <span class="detail-label">{{ t('myReservations.description') }}:</span>
                <span>{{ record.stageDesc }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">{{ t('myReservations.recordTime') }}:</span>
                <span>{{ record.createTime }}</span>
              </div>
              <div v-if="record.images && record.images.length > 0">
                <div class="images-grid">
                  <div v-for="image in record.images" :key="image.imageId" class="image-item">
                    <img :src="image.imageUrl" class="detail-image" @click="previewMedia(image.imageUrl, image.fileType)" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- After Construction -->
          <div v-if="currentDetail.constructionStage.afterRecords && currentDetail.constructionStage.afterRecords.length > 0">
            <h4>{{ t('myReservations.afterConstruction') }}</h4>
            <div v-for="record in currentDetail.constructionStage.afterRecords" :key="record.recordId" class="construction-record">
              <div class="detail-row">
                <span class="detail-label">{{ t('myReservations.description') }}:</span>
                <span>{{ record.stageDesc }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">{{ t('myReservations.recordTime') }}:</span>
                <span>{{ record.createTime }}</span>
              </div>
              <div v-if="record.images && record.images.length > 0">
                <div class="images-grid">
                  <div v-for="image in record.images" :key="image.imageId" class="image-item">
                    <img :src="image.imageUrl" class="detail-image" @click="previewMedia(image.imageUrl, image.fileType)" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Acceptance Stage -->
        <div class="detail-section" v-if="currentDetail.acceptanceStage">
          <h3>{{ t('myReservations.acceptanceStage') }}</h3>
          <div class="detail-row">
            <span class="detail-label">{{ t('myReservations.completionDate') }}:</span>
            <span>{{ currentDetail.acceptanceStage.completionDate }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ t('myReservations.chargeAmount') }}:</span>
            <span class="amount">NT$ {{ currentDetail.acceptanceStage.chargeAmount }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ t('myReservations.paymentMethod') }}:</span>
            <span>{{ currentDetail.acceptanceStage.paymentMethod }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ t('myReservations.collectionAmount') }}:</span>
            <span class="amount">NT$ {{ currentDetail.acceptanceStage.collectionAmount }}</span>
          </div>
          <div class="detail-row" v-if="currentDetail.acceptanceStage.remark">
            <span class="detail-label">{{ t('myReservations.remark') }}:</span>
            <span>{{ currentDetail.acceptanceStage.remark }}</span>
          </div>
          <div v-if="currentDetail.acceptanceStage.images && currentDetail.acceptanceStage.images.length > 0" style="margin-top: 8px;">
            <div class="images-grid">
              <div v-for="image in currentDetail.acceptanceStage.images" :key="image.imageId" class="image-item">
                <img :src="image.imageUrl" class="detail-image" @click="previewMedia(image.imageUrl, image.fileType)" />
              </div>
            </div>
          </div>
          <div v-if="currentDetail.acceptanceStage.customerSignature" style="margin-top: 16px;">
            <div class="detail-label">{{ t('myReservations.customerSignature') }}:</div>
            <img :src="currentDetail.acceptanceStage.customerSignature.imageUrl" class="signature-image" @click="previewMedia(currentDetail.acceptanceStage.customerSignature.imageUrl)" />
          </div>
        </div>

        <!-- Review Section (for COMPLETED/CLOSED status) -->
        <div class="detail-section review-section" v-if="(currentDetail.status === 'COMPLETED' || currentDetail.status === 'CLOSED') && currentDetail.reviewStage">
          <h3>{{ t('myReservations.reviewSection') }}</h3>
          
          <!-- Both reviewed - show all reviews -->
          <template v-if="currentDetail.reviewStage.technicianReviewed && currentDetail.reviewStage.customerReviewed">
            <!-- Technician's review to customer -->
            <div class="review-block" v-if="currentDetail.reviewStage.technicianToCustomer">
              <h4 class="review-block-title">{{ t('myReservations.technicianReview') }}</h4>
              <div class="review-card technician-review">
                <div class="review-header">
                  <div class="reviewer-info">
                    <span class="reviewer-name">{{ currentDetail.reviewStage.technicianToCustomer.reviewerName || t('myReservations.technician') }}</span>
                    <span class="review-time">{{ currentDetail.reviewStage.technicianToCustomer.createTime }}</span>
                  </div>
                  <el-rate :model-value="currentDetail.reviewStage.technicianToCustomer.rating" disabled size="small" />
                </div>
                <div class="review-content" v-if="currentDetail.reviewStage.technicianToCustomer.content">
                  {{ currentDetail.reviewStage.technicianToCustomer.content }}
                </div>
              </div>
            </div>

            <!-- Customer's review to technician -->
            <div class="review-block" v-if="currentDetail.reviewStage.customerToTechnician">
              <h4 class="review-block-title">{{ t('myReservations.customerReview') }}</h4>
              <div class="review-card customer-review">
                <div class="review-header">
                  <div class="reviewer-info">
                    <span class="reviewer-name">{{ t('myReservations.myReview') }}</span>
                    <span class="review-time">{{ currentDetail.reviewStage.customerToTechnician.createTime }}</span>
                  </div>
                  <el-rate :model-value="currentDetail.reviewStage.customerToTechnician.rating" disabled size="small" />
                </div>
                <div class="review-content" v-if="currentDetail.reviewStage.customerToTechnician.content">
                  {{ currentDetail.reviewStage.customerToTechnician.content }}
                </div>
              </div>
            </div>
          </template>

          <!-- Customer has reviewed but technician hasn't - show waiting message -->
          <template v-else-if="currentDetail.reviewStage.customerReviewed && !currentDetail.reviewStage.technicianReviewed">
            <div class="review-pending-notice">
              <el-icon><Star /></el-icon>
              <span>{{ t('myReservations.reviewSubmittedWaiting') }}</span>
            </div>
          </template>

          <!-- Customer hasn't reviewed yet - show review form -->
          <template v-else>
            <div class="no-review">
              <p>{{ t('myReservations.noReviewYet') }}</p>
              <el-button type="primary" @click="showReviewDialog">
                <el-icon><Star /></el-icon>
                {{ t('myReservations.writeReview') }}
              </el-button>
            </div>
          </template>

          <!-- Technician stats (only show when both reviewed) -->
          <div class="review-stats" v-if="currentDetail.reviewStage.technicianReviewed && currentDetail.reviewStage.customerReviewed && currentDetail.reviewStage.technicianAvgRating">
            <div class="stats-title">{{ t('myReservations.technicianStats') }}</div>
            <div class="stats-content">
              <div class="stat-item">
                <span class="stat-label">{{ t('myReservations.avgRating') }}:</span>
                <el-rate :model-value="currentDetail.reviewStage.technicianAvgRating" disabled size="small" show-score />
              </div>
              <div class="stat-item">
                <span class="stat-label">{{ t('myReservations.totalReviews') }}:</span>
                <span class="stat-value">{{ currentDetail.reviewStage.technicianReviewCount }} {{ t('myReservations.reviewCount') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- Media Preview Dialog (Images and Videos) -->
    <el-dialog
      v-model="mediaPreviewVisible"
      width="90%"
      style="max-width: 1200px"
      :show-close="true"
      append-to-body
      destroy-on-close
    >
      <div class="media-preview-container">
        <video v-if="isPreviewVideo" :src="currentPreviewUrl" controls autoplay class="preview-video"></video>
        <img v-else :src="currentPreviewUrl" alt="Preview" class="preview-image" />
      </div>
    </el-dialog>

    <!-- Reject Quotation Dialog -->
    <el-dialog
      v-model="rejectDialogVisible"
      :title="t('myReservations.rejectQuotation')"
      width="90%"
      style="max-width: 500px"
      append-to-body
    >
      <el-form label-position="top">
        <el-form-item :label="t('myReservations.rejectReason')">
          <el-input
            v-model="rejectReason"
            type="textarea"
            :rows="4"
            :placeholder="t('myReservations.rejectReasonPlaceholder')"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="rejectDialogVisible = false">{{ t('myReservations.cancel') }}</el-button>
        <el-button type="danger" @click="rejectQuotation" :loading="rejectLoading">{{ t('myReservations.confirmReject') }}</el-button>
      </template>
    </el-dialog>

    <!-- Review Dialog -->
    <el-dialog
      v-model="reviewDialogVisible"
      :title="t('myReservations.writeReview')"
      width="90%"
      style="max-width: 500px"
      append-to-body
    >
      <el-form label-position="top">
        <el-form-item :label="t('myReservations.rating')" required>
          <div class="rating-container">
            <el-rate v-model="reviewForm.rating" :texts="ratingTexts" show-text size="large" />
          </div>
        </el-form-item>
        <el-form-item :label="t('myReservations.reviewContent')">
          <el-input
            v-model="reviewForm.content"
            type="textarea"
            :rows="4"
            :placeholder="t('myReservations.reviewContentPlaceholder')"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="reviewDialogVisible = false">{{ t('myReservations.cancel') }}</el-button>
        <el-button type="primary" @click="submitReview" :loading="reviewLoading">{{ t('myReservations.submitReview') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { ArrowDown, Loading, Phone, Message, HomeFilled, Check, Close, WarningFilled, Star, VideoPlay } from '@element-plus/icons-vue'
import { ElMessageBox } from 'element-plus'
import axios from 'axios'

const { t, locale } = useI18n()

const searchForm = reactive({
  phone: '',
  email: ''
})
const loading = ref(false)
const reservations = ref<any[]>([])
const detailDialogVisible = ref(false)
const currentDetail = ref<any>(null)
const mediaPreviewVisible = ref(false)
const currentPreviewUrl = ref('')
const isPreviewVideo = ref(false)
const rejectDialogVisible = ref(false)
const rejectReason = ref('')
const confirmLoading = ref(false)
const rejectLoading = ref(false)
const currentOrderId = ref<number | null>(null)
const reviewDialogVisible = ref(false)
const reviewLoading = ref(false)
const reviewForm = reactive({
  rating: 5,
  content: ''
})

// Rating texts for display
const ratingTexts = computed(() => [
  t('myReservations.ratingTexts.1'),
  t('myReservations.ratingTexts.2'),
  t('myReservations.ratingTexts.3'),
  t('myReservations.ratingTexts.4'),
  t('myReservations.ratingTexts.5')
])

const currentLanguageName = computed(() => {
  return t(`language.${locale.value.replace('-', '')}`)
})

const handleLanguageChange = (lang: string) => {
  locale.value = lang
}

onMounted(() => {
  // Auto search if phone is provided in query
  const urlParams = new URLSearchParams(window.location.search)
  const phone = urlParams.get('phone')
  if (phone) {
    searchForm.phone = phone
    searchReservations()
  }
})

const searchReservations = async () => {
  // Both phone and email must be filled
  if (!searchForm.phone || !searchForm.email) {
    ElMessage.warning(t('myReservations.enterSearchCriteria'))
    return
  }

  loading.value = true
  try {
    const langMap: Record<string, string> = {
      'zh-TW': 'zh_TW',
      'zh-CN': 'zh_CN'
    }
    const apiLang = langMap[locale.value] || 'zh_TW'

    // Build query params - both phone and email are required
    const params: any = { 
      lang: apiLang,
      phone: searchForm.phone,
      email: searchForm.email
    }

    const response = await axios.get('/api/reservation/list', {
      params,
      headers: {
        'Accept-Language': apiLang
      }
    })

    if (response.data.code === 200) {
      reservations.value = response.data.data?.list || []
      if (reservations.value.length === 0) {
        ElMessage.info(t('myReservations.noData'))
      }
    } else {
      ElMessage.error(response.data.message || t('myReservations.searchFailed'))
    }
  } catch (error: any) {
    console.error('Search error:', error)
    ElMessage.error(error.response?.data?.message || t('myReservations.searchFailed'))
  } finally {
    loading.value = false
  }
}

const viewDetail = async (reservationId: number) => {
  try {
    const langMap: Record<string, string> = {
      'zh-TW': 'zh_TW',
      'zh-CN': 'zh_CN'
    }
    const apiLang = langMap[locale.value] || 'zh_TW'

    // Get reservation detail
    const detailResponse = await axios.get(`/api/reservation/${reservationId}`, {
      params: { lang: apiLang },
      headers: { 'Accept-Language': apiLang }
    })

    if (detailResponse.data.code === 200) {
      currentDetail.value = detailResponse.data.data
      // Store orderId for confirm/reject operations
      currentOrderId.value = currentDetail.value.orderId || currentDetail.value.orderDemand?.orderId || reservationId
      detailDialogVisible.value = true
    }
  } catch (error: any) {
    console.error('Get detail error:', error)
    ElMessage.error(error.response?.data?.message || t('myReservations.loadFailed'))
  }
}

const getStatusType = (status: string) => {
  const statusMap: Record<string, any> = {
    'PENDING_REVIEW': 'info',
    'PENDING': 'warning',
    'PENDING_TECHNICIAN': 'warning',
    'QUOTED': 'primary',
    'PENDING_CUSTOMER_CONFIRM': 'warning',
    'MATERIAL_PREPARING': 'primary',
    'PENDING_CONSTRUCTION': 'warning',
    'CONSTRUCTING': 'primary',
    'PENDING_ACCEPTANCE': 'warning',
    'ACCEPTANCE': 'primary',
    'COMPLETED': 'success',
    'CLOSED': 'success',
    'CANCELLED': 'info'
  }
  return statusMap[status] || 'info'
}

const getStatusText = (status: string) => {
  return t(`myReservations.status.${status}`)
}

const formatFileSize = (bytes: number) => {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

// Check if file is video by fileType
const isVideo = (fileType: string | undefined) => {
  return fileType?.startsWith('video/') || false
}

// Preview media (image or video)
const previewMedia = (url: string, fileType?: string) => {
  currentPreviewUrl.value = url
  isPreviewVideo.value = isVideo(fileType)
  mediaPreviewVisible.value = true
}

const goToHome = () => {
  window.location.href = '/'
}

// Confirm quotation
const confirmQuotation = async () => {
  if (!currentDetail.value) return
  
  try {
    await ElMessageBox.confirm(
      t('myReservations.confirmQuotationMessage'),
      t('myReservations.confirmQuotation'),
      {
        confirmButtonText: t('myReservations.confirm'),
        cancelButtonText: t('myReservations.cancel'),
        type: 'warning'
      }
    )
    
    confirmLoading.value = true
    const langMap: Record<string, string> = {
      'zh-TW': 'zh_TW',
      'zh-CN': 'zh_CN'
    }
    const apiLang = langMap[locale.value] || 'zh_TW'
    
    const response = await axios.post(
      `/api/reservation/customer/confirm-quotation?lang=${apiLang}`,
      {
        orderId: currentOrderId.value,
        phone: searchForm.phone,
        email: searchForm.email
      },
      {
        headers: { 'Accept-Language': apiLang }
      }
    )
    
    if (response.data.code === 200) {
      ElMessage.success(t('myReservations.confirmSuccess'))
      detailDialogVisible.value = false
      // Refresh the list
      searchReservations()
    } else {
      ElMessage.error(response.data.message || t('myReservations.confirmFailed'))
    }
  } catch (error: any) {
    if (error !== 'cancel') {
      console.error('Confirm quotation error:', error)
      ElMessage.error(error.response?.data?.message || t('myReservations.confirmFailed'))
    }
  } finally {
    confirmLoading.value = false
  }
}

// Show reject dialog
const showRejectDialog = () => {
  rejectReason.value = ''
  rejectDialogVisible.value = true
}

// Reject quotation
const rejectQuotation = async () => {
  if (!currentDetail.value) return
  
  if (!rejectReason.value.trim()) {
    ElMessage.warning(t('myReservations.rejectReasonRequired'))
    return
  }
  
  rejectLoading.value = true
  try {
    const langMap: Record<string, string> = {
      'zh-TW': 'zh_TW',
      'zh-CN': 'zh_CN'
    }
    const apiLang = langMap[locale.value] || 'zh_TW'
    
    const response = await axios.post(
      `/api/reservation/customer/reject-quotation?lang=${apiLang}`,
      {
        orderId: currentOrderId.value,
        phone: searchForm.phone,
        email: searchForm.email,
        reason: rejectReason.value.trim()
      },
      {
        headers: { 'Accept-Language': apiLang }
      }
    )
    
    if (response.data.code === 200) {
      ElMessage.success(t('myReservations.rejectSuccess'))
      rejectDialogVisible.value = false
      detailDialogVisible.value = false
      // Refresh the list
      searchReservations()
    } else {
      ElMessage.error(response.data.message || t('myReservations.rejectFailed'))
    }
  } catch (error: any) {
    console.error('Reject quotation error:', error)
    ElMessage.error(error.response?.data?.message || t('myReservations.rejectFailed'))
  } finally {
    rejectLoading.value = false
  }
}

// Show review dialog
const showReviewDialog = () => {
  reviewForm.rating = 5
  reviewForm.content = ''
  reviewDialogVisible.value = true
}

// Submit review
const submitReview = async () => {
  if (!currentOrderId.value) return
  
  if (reviewForm.rating < 1 || reviewForm.rating > 5) {
    ElMessage.warning(t('myReservations.ratingRequired'))
    return
  }
  
  reviewLoading.value = true
  try {
    const langMap: Record<string, string> = {
      'zh-TW': 'zh_TW',
      'zh-CN': 'zh_CN'
    }
    const apiLang = langMap[locale.value] || 'zh_TW'
    
    const response = await axios.post(
      `/api/reservation/customer/review-technician?lang=${apiLang}`,
      {
        orderId: currentOrderId.value,
        phone: searchForm.phone,
        email: searchForm.email,
        rating: reviewForm.rating,
        content: reviewForm.content.trim() || undefined
      },
      {
        headers: { 'Accept-Language': apiLang }
      }
    )
    
    if (response.data.code === 200) {
      ElMessage.success(t('myReservations.reviewSuccess'))
      reviewDialogVisible.value = false
      // Refresh detail to show the review
      if (currentOrderId.value) {
        viewDetail(currentOrderId.value)
      }
    } else {
      ElMessage.error(response.data.message || t('myReservations.reviewFailed'))
    }
  } catch (error: any) {
    console.error('Submit review error:', error)
    ElMessage.error(error.response?.data?.message || t('myReservations.reviewFailed'))
  } finally {
    reviewLoading.value = false
  }
}
</script>

<style scoped>
.my-reservations-wrapper {
  min-height: 100vh;
  background: #f0ebf8;
  padding: 20px 0;
}

.top-navigation {
  max-width: 760px;
  margin: 0 auto 20px;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.nav-left .el-button {
  color: #673ab7;
  font-weight: 500;
  font-size: 15px;
}

.nav-left .el-button:hover {
  color: #5e35b1;
}

.nav-left .el-icon {
  margin-right: 4px;
}

.nav-right {
  display: flex;
  align-items: center;
}

.el-dropdown-link {
  cursor: pointer;
  color: #673ab7;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
}

.reservations-container {
  max-width: 760px;
  margin: 0 auto;
  padding: 0 20px;
}

.page-header {
  background: #673ab7;
  color: #fff;
  padding: 32px 24px;
  border-radius: 8px 8px 0 0;
  text-align: center;
  margin-bottom: 0;
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 14px;
  color: #f5f5f5;
  margin: 0;
  line-height: 1.6;
}

.search-form-section {
  background: #fff;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-bottom: 24px;
}

.search-form {
  max-width: 100%;
}

.loading-container {
  text-align: center;
  padding: 60px 0;
  color: #673ab7;
}

.reservations-list {
  display: grid;
  gap: 20px;
  margin-bottom: 40px;
}

.reservation-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
}

.reservation-id {
  font-weight: 600;
  color: #303133;
}

.card-body {
  padding: 20px;
}

.info-row {
  display: flex;
  padding: 8px 0;
  border-bottom: 1px dashed #e4e7ed;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row .label {
  font-weight: 600;
  color: #606266;
  min-width: 120px;
}

.info-row .value {
  color: #303133;
  flex: 1;
}

.card-footer {
  padding: 16px 20px;
  background: #f5f7fa;
  text-align: right;
}

.empty-state {
  padding: 60px 0;
}

.detail-content {
  max-height: 70vh;
  overflow-y: auto;
}

.detail-section {
  margin-bottom: 24px;
}

.detail-section h3 {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #673ab7;
}

.detail-row {
  display: flex;
  padding: 12px 0;
  border-bottom: 1px solid #e4e7ed;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 600;
  color: #606266;
  min-width: 150px;
}

.amount {
  color: #e6a23c;
  font-weight: 600;
  font-size: 16px;
}

.amount-total {
  color: #f56c6c;
  font-weight: 700;
  font-size: 18px;
}

.quote-item,
.material-item,
.construction-record {
  background: #f9f9f9;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  border-left: 4px solid #673ab7;
}

.detail-section h4 {
  font-size: 16px;
  font-weight: 600;
  color: #673ab7;
  margin: 16px 0 12px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid #e4e7ed;
}

.signature-image {
  max-width: 300px;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
  display: block;
  margin-top: 8px;
}

.signature-image:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.image-item {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  transition: box-shadow 0.2s;
}

.image-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.detail-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.2s;
  display: block;
}

.detail-image:hover {
  transform: scale(1.05);
}

.image-info {
  padding: 8px 12px;
  background: #f5f7fa;
  border-top: 1px solid #e4e7ed;
}

.image-name {
  font-size: 13px;
  color: #303133;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.image-size {
  font-size: 12px;
  color: #909399;
}

.media-preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
}

.preview-image {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  display: block;
}

.preview-video {
  max-width: 100%;
  max-height: 80vh;
}

/* Video thumbnail styles */
.media-item {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  transition: box-shadow 0.2s;
}

.media-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.video-thumbnail {
  position: relative;
  width: 100%;
  height: 200px;
  cursor: pointer;
  background: #000;
}

.detail-video-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 30px;
  transition: background 0.2s;
}

.video-thumbnail:hover .play-icon {
  background: rgba(103, 58, 183, 0.8);
}

/* Confirm actions section */
.confirm-actions-section {
  background: #fff8e6;
  border: 2px solid #e6a23c;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
}

.confirm-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e6a23c;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
}

.confirm-notice .el-icon {
  font-size: 24px;
}

.confirm-buttons {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.confirm-buttons .el-button {
  min-width: 140px;
}

/* Review section */
.review-section {
  background: #f0f9eb;
  border: 2px solid #67c23a;
  border-radius: 8px;
  padding: 20px;
}

.review-section h3 {
  color: #67c23a;
  border-bottom-color: #67c23a;
}

.review-block {
  margin-bottom: 20px;
}

.review-block:last-child {
  margin-bottom: 0;
}

.review-block-title {
  font-size: 14px;
  color: #606266;
  margin: 0 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 1px dashed #dcdfe6;
}

.review-card {
  background: #fff;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.technician-review {
  border-left: 3px solid #409eff;
}

.customer-review {
  border-left: 3px solid #67c23a;
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.reviewer-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.reviewer-name {
  font-weight: 600;
  color: #303133;
}

.review-time {
  font-size: 12px;
  color: #909399;
}

.review-content {
  color: #606266;
  line-height: 1.6;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 4px;
}

.review-stats {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #dcdfe6;
}

.stats-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
}

.stats-content {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stat-label {
  color: #606266;
  font-size: 14px;
}

.stat-value {
  color: #303133;
  font-weight: 500;
}

.no-review {
  text-align: center;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
}

.no-review p {
  color: #606266;
  margin-bottom: 16px;
}

.review-pending-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  color: #e6a23c;
  font-size: 14px;
}

.review-pending-notice .el-icon {
  font-size: 20px;
}

.rating-container {
  padding: 8px 0;
}

@media (max-width: 768px) {
  .page-title {
    font-size: 24px;
  }

  .info-row {
    flex-direction: column;
  }

  .info-row .label {
    margin-bottom: 4px;
  }

  .detail-row {
    flex-direction: column;
  }

  .detail-label {
    margin-bottom: 4px;
  }

  .images-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }

  .detail-image {
    height: 100px;
  }

  .confirm-buttons {
    flex-direction: column;
  }

  .confirm-buttons .el-button {
    width: 100%;
  }
}
</style>
